import { useEffect, useState, type ReactNode } from "react";
import { Check, Clock, Handshake, Loader2, Lock, Star } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useApp } from "@/contexts/AppContext";

interface Deal {
  id: string;
  listing_id: string;
  buyer_id: string;
  seller_id: string;
  conversation_id: string | null;
  buyer_answer: "yes" | "ongoing" | "no" | null;
  seller_answer: "yes" | "no" | null;
  status: "open" | "buyer_yes" | "confirmed" | "no" | "expired";
  asks: number;
  listing?: { title: string } | null;
}

interface Props {
  /** card dentro do chat */
  id?: string;
  /** card na pagina do anuncio (so para o cliente, depois da 1a pergunta) */
  listingId?: string;
  className?: string;
}

function Shell({ children, tone = "amber", className = "" }: { children: ReactNode; tone?: "amber" | "green" | "muted"; className?: string }) {
  const bar = tone === "green" ? "from-[#2d7a4d] to-[#1a4a2e]" : tone === "muted" ? "from-[#d8d3c6] to-[#d8d3c6]" : "from-[#e0a021] to-[#2d7a4d]";
  return (
    <div className={`relative overflow-hidden rounded-2xl border border-[#ebe7dc] bg-white p-3.5 shadow-[0_6px_18px_rgba(26,74,46,0.08)] ${className}`}>
      <span className={`absolute inset-y-0 left-0 w-1 bg-gradient-to-b ${bar}`} />
      {children}
    </div>
  );
}

function Head({ icon, title, sub, green }: { icon: ReactNode; title: string; sub?: string; green?: boolean }) {
  return (
    <div className="flex items-center gap-3">
      <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${green ? "bg-[#eaf2ec] text-[#2d7a4d]" : "bg-[#fbf1dc] text-[#b37d12]"}`}>{icon}</span>
      <div className="min-w-0">
        <p className="text-sm font-bold text-[#1a4a2e]">{title}</p>
        {sub && <p className="truncate text-xs text-[#8a9088]">{sub}</p>}
      </div>
    </div>
  );
}

const btn = "flex items-center justify-center gap-1.5 rounded-xl px-3 py-2.5 text-[13px] font-semibold transition disabled:opacity-60";

export default function DealCheckCard({ id, listingId, className = "" }: Props) {
  const { user } = useApp();
  const [deal, setDeal] = useState<Deal | null>(null);
  const [reviewed, setReviewed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [justOngoing, setJustOngoing] = useState(false);
  const [later, setLater] = useState(false);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");

  async function load() {
    if (!user) return;
    let q = supabase.from("deal_checks").select("*, listing:listings(title)");
    if (id) q = q.eq("id", id);
    else if (listingId) q = q.eq("listing_id", listingId).eq("buyer_id", user.id).gt("asks", 0);
    else return;
    const { data } = await q.maybeSingle();
    const d = data as Deal | null;
    setDeal(d);
    if (d && d.buyer_id === user.id) {
      const { data: rv } = await supabase.from("reviews").select("id").eq("listing_id", d.listing_id).eq("reviewer_id", user.id).maybeSingle();
      setReviewed(!!rv);
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [id, listingId, user?.id]);

  async function answer(a: "yes" | "ongoing" | "no") {
    if (!deal) return;
    setBusy(true); setError("");
    const { data, error: err } = await supabase.rpc("answer_deal_check", { p_id: deal.id, p_answer: a });
    setBusy(false);
    if (err) { setError("Não foi possível registrar. Tente de novo."); return; }
    const r = data as { notify_seller?: boolean; seller_id?: string } | null;
    if (r?.notify_seller && r.seller_id) {
      supabase.functions.invoke("notify-new-message", {
        body: { recipient_id: r.seller_id, message_preview: "O cliente informou que vocês fecharam negócio. Toque para confirmar." },
      });
    }
    if (a === "ongoing") setJustOngoing(true);
    load();
  }

  async function sendReview() {
    if (!deal) return;
    if (!rating) { setError("Escolha de 1 a 5 estrelas."); return; }
    if (comment.trim().length < 3) { setError("Escreva um comentário curto."); return; }
    setBusy(true); setError("");
    const { error: err } = await supabase.rpc("submit_review", { p_listing_id: deal.listing_id, p_rating: rating, p_comment: comment.trim() });
    setBusy(false);
    if (err) {
      setError(err.message?.includes("already_reviewed") ? "Você já avaliou este anúncio." : "Não foi possível enviar a avaliação.");
      if (err.message?.includes("already_reviewed")) setReviewed(true);
      return;
    }
    setReviewed(true);
    window.dispatchEvent(new Event("agro:review-submitted"));
  }

  if (!user || !deal) return null;
  const title = deal.listing?.title || "";
  const isBuyer = user.id === deal.buyer_id;
  const isSeller = user.id === deal.seller_id;
  if (!isBuyer && !isSeller) return null;

  const confirmedLine = (
    <div className="mt-3 flex items-center gap-1.5 rounded-lg bg-[#eaf2ec] px-2.5 py-2 text-[11.5px] font-medium text-[#2d7a4d]">
      <Check className="h-3.5 w-3.5" /> Negócio confirmado pelos dois
    </div>
  );

  /* ================= CLIENTE ================= */
  if (isBuyer) {
    // pergunta aberta
    if (deal.status === "open" && deal.buyer_answer !== "yes" && deal.buyer_answer !== "no") {
      if (justOngoing) {
        return (
          <div className={`flex justify-center ${className}`}>
            <span className="rounded-full border border-[#ebe7dc] bg-white px-3 py-1.5 text-[11.5px] text-[#8a9088]">
              Combinado! Perguntamos de novo em 7 dias.
            </span>
          </div>
        );
      }
      return (
        <Shell className={className}>
          <Head icon={<Handshake className="h-[18px] w-[18px]" />} title="Vocês fecharam negócio?" sub={title} />
          <button onClick={() => answer("yes")} disabled={busy} className={`${btn} mt-3 w-full bg-[#2d7a4d] text-white hover:bg-[#1a4a2e]`}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Sim, fechamos
          </button>
          <div className="mt-2 flex flex-wrap gap-2">
            <button onClick={() => answer("ongoing")} disabled={busy} className={`${btn} min-w-[124px] flex-1 whitespace-nowrap border border-[#2d7a4d] bg-white text-[#2d7a4d] hover:bg-[#eaf2ec]`}>Ainda negociando</button>
            <button onClick={() => answer("no")} disabled={busy} className={`${btn} min-w-[124px] flex-1 whitespace-nowrap border border-[#ebe7dc] bg-white text-[#8a9088] hover:bg-[#f7f5ef]`}>Não fechamos</button>
          </div>
          {error && <p className="mt-2 text-center text-xs text-[#c0493b]">{error}</p>}
          <p className="mt-2.5 flex items-center justify-center gap-1 text-[10.5px] text-[#8a9088]"><Lock className="h-3 w-3" /> Só você e o anunciante veem esta pergunta</p>
        </Shell>
      );
    }

    // disse que fechou
    if (deal.buyer_answer === "yes") {
      const statusLine =
        deal.status === "confirmed" ? confirmedLine :
        deal.seller_answer === "no" ? null :
        (
          <div className="mt-3 flex items-center gap-1.5 rounded-lg bg-[#fbf1dc] px-2.5 py-2 text-[11.5px] text-[#94680f]">
            <Clock className="h-3.5 w-3.5" /> Aguardando o anunciante confirmar para ganhar o selo
          </div>
        );

      if (!reviewed && !later) {
        return (
          <Shell className={className}>
            <Head green icon={<Check className="h-[18px] w-[18px]" />} title="Que bom que deu certo!" sub={`Como foi? ${title}`} />
            <div className="my-3 flex justify-center gap-1.5" onMouseLeave={() => setHover(0)}>
              {[1, 2, 3, 4, 5].map((s) => (
                <button key={s} type="button" onMouseEnter={() => setHover(s)} onClick={() => setRating(s)} aria-label={`${s} estrelas`}>
                  <Star className={`h-8 w-8 transition-colors ${s <= (hover || rating) ? "fill-[#e0a021] text-[#e0a021]" : "text-[#e3ddcf]"}`} />
                </button>
              ))}
            </div>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={3}
              maxLength={1000}
              placeholder="Conte como foi o aluguel..."
              className="w-full rounded-xl border border-[#ebe7dc] bg-[#f7f5ef] px-3 py-2.5 text-sm outline-none focus:border-[#2d7a4d]"
            />
            <button onClick={sendReview} disabled={busy} className={`${btn} mt-2 w-full bg-[#2d7a4d] text-white hover:bg-[#1a4a2e]`}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Enviar avaliação
            </button>
            <button onClick={() => setLater(true)} className="mt-1.5 w-full py-1.5 text-xs font-medium text-[#8a9088] hover:text-[#1a4a2e]">Avaliar depois</button>
            {error && <p className="mt-1 text-center text-xs text-[#c0493b]">{error}</p>}
            {statusLine}
          </Shell>
        );
      }

      if (listingId && !id) return null; // na pagina do anuncio, some depois de avaliar
      return (
        <Shell tone="green" className={className}>
          <Head green icon={<Check className="h-[18px] w-[18px]" />} title={deal.status === "confirmed" ? "Negócio confirmado" : "Negócio informado"} sub={title} />
          <p className="mt-2 text-xs text-[#8a9088]">
            {reviewed ? "Obrigado pela avaliação!" : "Você pode avaliar quando quiser na página do anúncio."}
          </p>
          {statusLine}
        </Shell>
      );
    }

    if (deal.buyer_answer === "no" && id) {
      return (
        <div className={`flex justify-center ${className}`}>
          <span className="rounded-full border border-[#ebe7dc] bg-white px-3 py-1.5 text-[11.5px] text-[#8a9088]">Tudo bem, obrigado por avisar.</span>
        </div>
      );
    }
    return null;
  }

  /* ================= ANUNCIANTE ================= */
  if (deal.status === "expired" || deal.buyer_answer === "no") return null;

  if (!deal.seller_answer) {
    const buyerSaidYes = deal.buyer_answer === "yes";
    return (
      <Shell className={className}>
        <Head
          icon={<Handshake className="h-[18px] w-[18px]" />}
          title={buyerSaidYes ? "O cliente disse que vocês fecharam negócio" : "Vocês fecharam negócio?"}
          sub={title}
        />
        <p className="mt-2 text-xs text-[#8a9088]">
          {buyerSaidYes ? "Confirme para a avaliação dele ganhar o selo “Negócio confirmado”." : "Sua confirmação conta no seu total de negócios fechados."}
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          <button onClick={() => answer("yes")} disabled={busy} className={`${btn} min-w-[124px] flex-1 whitespace-nowrap bg-[#2d7a4d] text-white hover:bg-[#1a4a2e]`}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Sim, fechamos
          </button>
          <button onClick={() => answer("no")} disabled={busy} className={`${btn} min-w-[124px] flex-1 whitespace-nowrap border border-[#ebe7dc] bg-white text-[#8a9088] hover:bg-[#f7f5ef]`}>Não fechamos</button>
        </div>
        {error && <p className="mt-2 text-center text-xs text-[#c0493b]">{error}</p>}
      </Shell>
    );
  }

  return (
    <Shell tone={deal.status === "confirmed" ? "green" : "muted"} className={className}>
      <Head green icon={<Check className="h-[18px] w-[18px]" />} title={deal.status === "confirmed" ? "Negócio confirmado" : "Resposta registrada"} sub={title} />
      {deal.status === "confirmed" ? confirmedLine : (
        <p className="mt-2 text-xs text-[#8a9088]">{deal.seller_answer === "yes" ? "Aguardando a resposta do cliente." : "Obrigado por responder."}</p>
      )}
    </Shell>
  );
}
